// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { Add, Close } from '@mui/icons-material';
import { Box, Button, Typography, IconButton, Card } from '@mui/material';

interface ListItem<T> {
  id: string;
  children?: T[];
}

interface DynamicListInputProps<T extends ListItem<T>> {
  title: string;
  list: T[];
  setList: React.Dispatch<React.SetStateAction<T[]>>;
  createNewItem: () => T;
  renderItem: (item: T, updateItem: (updatedItem: T) => void) => React.ReactNode;
}

const DynamicListInput = <T extends ListItem<T>>({
  title,
  list,
  setList,
  createNewItem,
  renderItem,
}: DynamicListInputProps<T>) => {
  const handleAddItem = () => {
    const newItem = createNewItem();
    setList([...list, newItem]);
  };

  const handleRemoveItem = (index: number) => {
    const newList = list.filter((_, i) => i !== index);
    setList(newList);
  };

  const handleUpdateItem = (index: number, updatedItem: T) => {
    const newList = [...list];
    newList[index] = updatedItem;
    setList(newList);
  };

  return (
    <Box sx={{ display: 'relative' }} mt={2}>
      <Typography mb={1} gutterBottom color="textPrimary">
        {title}
      </Typography>
      {list.map((item, index) => (
        <Card
          variant="outlined"
          sx={{ display: 'flex', flexDirection: 'column', width: '100%', mb: 2, pr: 1 }}
          key={item.id}
        >
          <Box sx={{ p: 1 }}>
            {renderItem(item, (updatedItem) => handleUpdateItem(index, updatedItem))}
          </Box>

          <Box sx={{ ml: 'auto' }}>
            <IconButton sx={{ m: 1 }} onClick={() => handleRemoveItem(index)}>
              <Close color="error" />
            </IconButton>
          </Box>
        </Card>
      ))}
      <Box sx={{ bottom: 0, width: '100%', display: 'flex' }}>
        <Button
          size="small"
          variant="contained"
          sx={{ ml: 'auto', textTransform: 'capitalize' }}
          onClick={handleAddItem}
          startIcon={<Add sx={{ color: 'white' }} />}
        >
          <Typography variant="caption">Add</Typography>
        </Button>
      </Box>
    </Box>
  );
};

export default DynamicListInput;
